import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, User, AlertCircle, Sparkles, Star, Trophy, BookOpen, TrendingUp, ChevronLeft, ChevronRight, Heart, MessageCircle, Send } from 'lucide-react';
import { useWechatShare } from '../hooks/useWechatShare';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface SharePage {
  pageNumber: number;
  content: string;
  imageUrl: string | null;
  audioUrl: string | null;
  recordingUrl: string | null;
  score: number | null;
  accuracy: number | null;
  fluency: number | null;
  completeness: number | null;
  transcript: string | null;
}

interface SharerInfo {
  id: string;
  nickname: string;
  avatar: string | null;
}

interface ShareStats {
  maxScore: number | null;
  totalWordCount: number;
  mispronouncedCount: number;
  coreVocabulary: string[];
}

interface ShareStory {
  id: string;
  title: string;
  summary: string | null;
  coverUrl: string | null;
  artStyle: string | null;
  targetAge: string | null;
  pageCount: number | null;
}

interface CommentItem {
  id: string;
  shareToken: string;
  content: string;
  nickname: string | null;
  avatar: string | null;
  createdAt: string;
}

interface ShareData {
  story: ShareStory;
  pages: SharePage[];
  totalPages: number;
  sharer: SharerInfo | null;
  isShared: boolean;
  stats: ShareStats;
  likeCount: number;
  playCount: number;
  commentCount: number;
  tags: string[];
  comments: CommentItem[];
}

/**
 * 根据英文单词返回对应的 emoji
 */
function getWordEmoji(word: string): string {
  const map: Record<string, string> = {
    // 人物
    bella: '👧', tim: '👦', friend: '🤝', child: '👶', children: '👶', kid: '👶',
    boy: '👦', girl: '👧', baby: '👶', man: '👨', woman: '👩', people: '👥',
    // 动作
    play: '🎮', walk: '🚶', run: '🏃', jump: '🦘', wake: '🌅', wakes: '🌅',
    sleep: '😴', eat: '🍽️', drink: '🥤', read: '📖', smile: '😊', laugh: '😂',
    cry: '😢', hug: '🤗', dance: '💃', sing: '🎤', swim: '🏊', fly: '🦋',
    // 天气/自然
    sun: '☀️', sunny: '☀️', rain: '🌧️', rainy: '🌧️', cloud: '☁️', cloudy: '☁️',
    snow: '❄️', snowy: '❄️', wind: '💨', windy: '💨', storm: '⛈️', rainbow: '🌈',
    star: '⭐', moon: '🌙', sky: '🌌', tree: '🌳', flower: '🌸', grass: '🌿',
    // 动物
    cat: '🐱', dog: '🐶', bird: '🐦', fish: '🐟', bear: '🐻', rabbit: '🐰',
    lion: '🦁', tiger: '🐯', elephant: '🐘', giraffe: '🦒', monkey: '🐵',
    panda: '🐼', duck: '🦆', pig: '🐷', cow: '🐄', horse: '🐴', sheep: '🐑',
    chicken: '🐔', frog: '🐸', snake: '🐍', butterfly: '🦋', bee: '🐝',
    // 食物
    apple: '🍎', banana: '🍌', orange: '🍊', grape: '🍇', watermelon: '🍉',
    strawberry: '🍓', cherry: '🍒', peach: '🍑', pear: '🍐', lemon: '🍋',
    bread: '🍞', cake: '🎂', cookie: '🍪', candy: '🍬', lollipop: '🍭',
    ice: '🍦', cream: '🍦', pizza: '🍕', burger: '🍔', egg: '🥚', milk: '🥛',
    // 物品
    ball: '⚽', book: '📚', toy: '🧸', bike: '🚲', car: '🚗', bus: '🚌',
    train: '🚂', plane: '✈️', boat: '🚢', house: '🏠', school: '🏫', park: '🏞️',
    bed: '🛏️', chair: '🪑', table: '🪑', door: '🚪', window: '🪟', clock: '⏰',
    phone: '📱', computer: '💻', tv: '📺', camera: '📷', lamp: '💡', bag: '🎒',
    shoe: '👟', hat: '🎩', dress: '👗', shirt: '👕', coat: '🧥', umbrella: '☂️',
    // 形容词
    big: '🔷', small: '🔹', happy: '😊', sad: '😢', angry: '😠', tired: '😫',
    fast: '⚡', slow: '🐢', hot: '🔥', cold: '❄️', warm: '🌡️', cool: '🆒',
    new: '✨', old: '📜', good: '👍', bad: '👎', beautiful: '💎', ugly: '👹',
    clean: '🧼', dirty: '🐽', loud: '🔊', quiet: '🤫', bright: '💡', dark: '🌑',
    // 颜色
    red: '🔴', blue: '🔵', green: '🟢', yellow: '🟡', purple: '🟣',
    pink: '💗', black: '⚫', white: '⚪', brown: '🟤', gray: '🔘', colour: '🎨',
    color: '🎨',
    // 方位/时间
    up: '⬆️', down: '⬇️', left: '⬅️', right: '➡️', in: '📥', out: '📤',
    on: '🔛', off: '📴', here: '📍', there: '📍', near: '📐', far: '🔭',
    today: '📅', tomorrow: '📅', yesterday: '📅', now: '⏰', morning: '🌅',
    afternoon: '🌇', evening: '🌆', night: '🌃',
    // 数字
    one: '1️⃣', two: '2️⃣', three: '3️⃣', four: '4️⃣', five: '5️⃣',
    six: '6️⃣', seven: '7️⃣', eight: '8️⃣', nine: '9️⃣', ten: '🔟',
    // 其他常见绘本词
    love: '❤️', heart: '❤️', family: '👨‍👩‍👧‍👦', home: '🏠', gift: '🎁',
    party: '🎉', birthday: '🎂', christmas: '🎄', halloween: '🎃',
    // 回退：按首字母分配默认 emoji
  };

  const lower = word.toLowerCase();
  if (map[lower]) return map[lower];

  // 按首字母回退
  const fallback: Record<string, string> = {
    a: '🅰️', b: '🅱️', c: '🌊', d: '🌙', e: '🥚', f: '🔥', g: '🍇', h: '🏠',
    i: '👁️', j: '🧃', k: '🔑', l: '🍋', m: '🌙', n: '📰', o: '🍊', p: '🍕',
    q: '👸', r: '🌹', s: '⭐', t: '🌲', u: '☂️', v: '🎻', w: '🌊', x: '❌',
    y: '🧶', z: '🦓',
  };
  return fallback[lower[0]] || '📖';
}

export default function ShareViewer() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingPage, setPlayingPage] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [playCount, setPlayCount] = useState(0);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [commentCount, setCommentCount] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [commentNickname, setCommentNickname] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const touchStartX = useRef(0);
  const hasCountedPlay = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!token) {
      setError('无效的分享链接');
      setLoading(false);
      return;
    }

    const fetchShareData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/share/${token}`);
        const result = await res.json();

        if (result.success) {
          setData(result.data);
          setLikeCount(result.data.likeCount || 0);
          setPlayCount(result.data.playCount || 0);
          setCommentCount(result.data.commentCount || 0);
          // 恢复本地点赞状态
          const localLiked = localStorage.getItem(`share_liked_${token}`);
          setLiked(localLiked === 'true');
        } else {
          setError(result.error || '分享内容加载失败');
        }
      } catch (err) {
        setError('网络错误，请检查连接');
      } finally {
        setLoading(false);
      }
    };

    fetchShareData();
  }, [token]);

  // 独立加载评论，减少首屏加载时间
  useEffect(() => {
    if (!token) return;
    const fetchComments = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/share/${token}/comments`);
        const result = await res.json();
        if (result.success) {
          setComments(result.data || []);
        }
      } catch (e) {
        console.error('Fetch comments failed:', e);
      }
    };
    fetchComments();
  }, [token]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // 微信JSSDK分享配置 - 必须放在所有条件 return 之前（Hooks 规则）
  useWechatShare(
    data
      ? {
          title: `${data.sharer?.nickname || '小朋友'} 正在读《${data.story.title}》`,
          desc: data.stats.maxScore !== null
            ? `来听听 ${data.sharer?.nickname || 'TA'} 读绘本吧~ 绘本跟读 ${data.stats.maxScore}分！`
            : `来听听 ${data.sharer?.nickname || 'TA'} 读绘本吧~`,
          link: window.location.href,
          imgUrl: data.story.coverUrl || `${window.location.origin}/logo.png`,
        }
      : null
  );

  const playRecording = async (pageNumber: number, url: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (playingPage === pageNumber) {
      setPlayingPage(null);
      return;
    }

    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setPlayingPage(null);
    audio.onerror = () => {
      setPlayingPage(null);
      alert('音频播放失败');
    };
    audio.play().catch(() => setPlayingPage(null));
    setPlayingPage(pageNumber);

    // 播放计数（每页每 session 只计一次）
    if (!hasCountedPlay.current.has(pageNumber)) {
      hasCountedPlay.current.add(pageNumber);
      // 乐观更新
      setPlayCount(prev => prev + 1);
      try {
        const res = await fetch(`${API_BASE_URL}/share/${token}/play`, { method: 'POST' });
        const result = await res.json();
        if (result.success && result.data?.playCount !== undefined) {
          setPlayCount(result.data.playCount);
        }
      } catch (e) {
        // 请求失败则回滚
        setPlayCount(prev => prev - 1);
      }
    }
  };

  const toggleLike = async () => {
    if (!token) return;
    const newLiked = !liked;
    const action = newLiked ? 'like' : 'unlike';
    // 乐观更新
    const previousLiked = liked;
    const previousLikeCount = likeCount;
    setLiked(newLiked);
    setLikeCount(prev => newLiked ? prev + 1 : Math.max(0, prev - 1));
    localStorage.setItem(`share_liked_${token}`, String(newLiked));
    try {
      const res = await fetch(`${API_BASE_URL}/share/${token}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const result = await res.json();
      if (result.success) {
        setLikeCount(result.data.likeCount);
      } else {
        // 失败回滚
        setLiked(previousLiked);
        setLikeCount(previousLikeCount);
        localStorage.setItem(`share_liked_${token}`, String(previousLiked));
      }
    } catch (e) {
      // 失败回滚
      setLiked(previousLiked);
      setLikeCount(previousLikeCount);
      localStorage.setItem(`share_liked_${token}`, String(previousLiked));
      console.error('Like failed:', e);
    }
  };

  const submitComment = async () => {
    if (!token || !commentText.trim()) return;
    setSubmittingComment(true);
    const tempComment: CommentItem = {
      id: `temp-${Date.now()}`,
      shareToken: token,
      content: commentText.trim(),
      nickname: commentNickname.trim() || null,
      avatar: null,
      createdAt: new Date().toISOString(),
    };
    // 乐观更新
    setComments(prev => [tempComment, ...prev]);
    setCommentCount(prev => prev + 1);
    const previousComments = comments;
    const previousCommentCount = commentCount;
    try {
      const res = await fetch(`${API_BASE_URL}/share/${token}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: commentText.trim(),
          nickname: commentNickname.trim() || null,
        }),
      });
      const result = await res.json();
      if (result.success) {
        // 用服务端返回的真实数据替换临时评论
        setComments(prev => prev.map(c => c.id === tempComment.id ? result.data.comment : c));
        setCommentCount(result.data.commentCount);
        setCommentText('');
      } else {
        // 失败回滚
        setComments(previousComments);
        setCommentCount(previousCommentCount);
      }
    } catch (e) {
      // 失败回滚
      setComments(previousComments);
      setCommentCount(previousCommentCount);
      console.error('Comment failed:', e);
    } finally {
      setSubmittingComment(false);
    }
  };

  const goNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage((p) => p + 1);
    }
  };

  const goPrev = () => {
    if (currentPage > 0) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-400 via-blue-300 to-blue-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-blue-400 via-blue-300 to-blue-100 p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">分享链接失效</h2>
          <p className="text-slate-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { story, pages, sharer, stats } = data;
  const hasScores = stats.maxScore !== null;

  // 模拟"超过xx%小朋友"（基于最高分）
  const percentile = hasScores ? Math.min(99, Math.max(10, stats.maxScore!)) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-400 via-blue-300 to-blue-100">
      {/* 顶部用户信息 */}
      <div className="w-full flex justify-center px-4 pt-6 pb-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {sharer?.avatar ? (
                <img src={sharer.avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-blue-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800 truncate">
                  {sharer?.nickname || '匿名用户'}
                </span>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                  历史最佳
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                来听听我读绘本吧~
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* 互动统计 */}
      <div className="w-full flex justify-center px-4 pb-3">
        <div className="w-full max-w-md flex flex-wrap items-center gap-3">
          <button
            onClick={toggleLike}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition ${
              liked
                ? 'bg-rose-100 text-rose-600'
                : 'bg-white/80 text-slate-600 hover:bg-white'
            }`}
          >
            <Heart className={`w-4 h-4 ${liked ? 'fill-rose-500 text-rose-500' : ''}`} />
            {likeCount}
          </button>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 text-slate-600 text-sm font-medium">
            <Play className="w-4 h-4" />
            {playCount} 次播放
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 text-slate-600 text-sm font-medium">
            <MessageCircle className="w-4 h-4" />
            {commentCount}
          </div>
          {data?.tags && data.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {data.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 rounded-md bg-white/60 text-slate-500 text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 封面 + 评分 */}
      <div className="w-full flex justify-center px-4 pb-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden"
        >
          <div className="relative">
            {story.coverUrl && (
              <div className="w-full aspect-[4/3] bg-slate-100">
                <img
                  src={story.coverUrl}
                  alt={story.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {/* 评分角标 */}
            {hasScores && (
              <div className="absolute top-3 right-3 bg-gradient-to-br from-orange-400 to-red-500 text-white rounded-xl px-3 py-1.5 shadow-lg">
                <div className="text-xs font-medium opacity-90">绘本跟读</div>
                <div className="text-xl font-bold">{stats.maxScore}分</div>
              </div>
            )}
          </div>
          <div className="p-4">
            <h1 className="text-xl font-bold text-slate-800 text-center">
              {story.title}
            </h1>
            {story.summary && (
              <p className="text-sm text-slate-500 text-center mt-1 line-clamp-2">
                {story.summary}
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* 精彩表现 */}
      {hasScores && (
        <div className="w-full flex justify-center px-4 pb-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="w-full max-w-md"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-5">
              {/* 标题 */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                <h2 className="text-lg font-bold text-slate-800">精彩表现</h2>
                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
              </div>

              {/* 统计项 */}
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <div className="flex items-center gap-2 text-slate-600">
                    <BookOpen className="w-4 h-4 text-blue-400" />
                    <span>阅读单词量</span>
                  </div>
                  <span className="text-blue-600 font-bold text-lg">{stats.totalWordCount}个</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Trophy className="w-4 h-4 text-amber-400" />
                    <span>绘本跟读最高分</span>
                  </div>
                  <span className="text-blue-600 font-bold text-lg">{stats.maxScore}分</span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2 text-slate-600">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span>超过了</span>
                  </div>
                  <span className="text-orange-500 font-bold text-lg">{percentile}%</span>
                </div>
              </div>

              {/* 超越提示 */}
              <div className="mt-4 bg-amber-50 rounded-xl p-3 flex items-center justify-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                <span className="text-amber-700 text-sm font-medium">
                  超越了 <span className="text-amber-600 font-bold text-base">{Math.round(percentile * 10000)}</span> 个小朋友
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* 核心词汇 */}
      {stats.coreVocabulary.length > 0 && (
        <div className="w-full flex justify-center px-4 pb-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="w-full max-w-md"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-5">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                <h2 className="text-lg font-bold text-slate-800">核心词汇</h2>
                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {stats.coreVocabulary.map((word, idx) => (
                  <motion.div
                    key={word}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + idx * 0.03 }}
                    className="bg-blue-50 rounded-xl p-3 text-center hover:bg-blue-100 transition"
                  >
                    <div className="text-2xl mb-1">
                      {getWordEmoji(word)}
                    </div>
                    <div className="text-slate-700 font-medium text-sm capitalize">{word}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* 绘本翻页阅读器 */}
      <div className="w-full flex justify-center px-4 pb-4">
        <div className="w-full max-w-md">
          {/* 页码指示器 */}
          <div className="flex justify-center items-center gap-2 mb-3">
            {pages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentPage(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === currentPage ? 'w-6 bg-white' : 'w-2 bg-white/40'
                }`}
              />
            ))}
          </div>

          {/* 滑动翻页区域 */}
          <div
            className="overflow-hidden rounded-2xl shadow-lg"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <motion.div
              className="flex"
              animate={{ x: `-${currentPage * 100}%` }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {pages.map((page) => (
                <div
                  key={page.pageNumber}
                  className="w-full flex-shrink-0 bg-white/95 backdrop-blur-sm"
                >
                  {/* 图片 */}
                  {page.imageUrl && (
                    <div className="w-full aspect-[4/3] bg-slate-100 relative">
                      <img
                        src={page.imageUrl}
                        alt={`第${page.pageNumber}页`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* 文字 */}
                  <div className="p-5">
                    <p className="text-slate-700 text-lg leading-relaxed text-center mb-4">
                      {page.content}
                    </p>

                    {/* 单页评分 */}
                    {page.score !== null && (
                      <div className="flex items-center gap-3 mb-3 bg-blue-50 rounded-lg px-3 py-2">
                        <div className="text-blue-600 font-bold text-lg">{page.score}分</div>
                        <div className="flex-1 flex gap-2 text-xs text-slate-500">
                          <span>准确度 {Math.round((page.accuracy || 0) * 100)}%</span>
                          <span>流利度 {Math.round((page.fluency || 0) * 100)}%</span>
                          <span>完整度 {Math.round((page.completeness || 0) * 100)}%</span>
                        </div>
                      </div>
                    )}

                    {/* 播放按钮 */}
                    {page.recordingUrl && (
                      <button
                        onClick={() => playRecording(page.pageNumber, page.recordingUrl!)}
                        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition ${
                          playingPage === page.pageNumber
                            ? 'bg-red-500 text-white'
                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                        }`}
                      >
                        {playingPage === page.pageNumber ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Volume2 className="w-5 h-5" />
                        )}
                      </button>
                    )}

                    {/* AI朗读 */}
                    {page.audioUrl && !page.recordingUrl && (
                      <button
                        onClick={() => playRecording(page.pageNumber, page.audioUrl!)}
                        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition ${
                          playingPage === page.pageNumber
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {playingPage === page.pageNumber ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* 翻页控制 */}
          <div className="flex items-center justify-between mt-4 px-2">
            <button
              onClick={goPrev}
              disabled={currentPage === 0}
              className="flex items-center gap-1 px-4 py-2 rounded-xl bg-white/90 text-blue-600 font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white transition"
            >
              <ChevronLeft className="w-5 h-5" />
              上一页
            </button>
            <span className="text-white font-medium text-sm">
              {currentPage + 1} / {pages.length}
            </span>
            <button
              onClick={goNext}
              disabled={currentPage === pages.length - 1}
              className="flex items-center gap-1 px-4 py-2 rounded-xl bg-white/90 text-blue-600 font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white transition"
            >
              下一页
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* 评论区域 */}
      <div className="w-full flex justify-center px-4 pb-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="w-full max-w-md"
        >
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-bold text-slate-800">评论 ({commentCount})</h2>
            </div>

            {/* 评论输入 */}
            <div className="flex flex-col gap-2 mb-4">
              <input
                type="text"
                placeholder="你的昵称（可选）"
                value={commentNickname}
                onChange={(e) => setCommentNickname(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                maxLength={30}
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="写下你的评论..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitComment()}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  maxLength={500}
                />
                <button
                  onClick={submitComment}
                  disabled={!commentText.trim() || submittingComment}
                  className="px-3 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {submittingComment ? (
                    <span className="text-sm">发送中</span>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* 评论列表 */}
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {comments.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-4">暂无评论，来说两句吧~</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {comment.avatar ? (
                        <img src={comment.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {comment.nickname || '匿名用户'}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(comment.createdAt).toLocaleString('zh-CN', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5 break-words">{comment.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* 底部CTA */}
      <div className="w-full flex justify-center px-4 pb-8 pt-2">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="w-full max-w-md text-center"
        >
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 text-white font-bold text-lg shadow-lg hover:shadow-xl transition"
          >
            <Sparkles className="w-6 h-6" />
            我也来试试
          </button>
          <p className="text-white/70 text-sm mt-3">
            AI绘本生成器 · 为孩子创造独一无二的绘本故事
          </p>
        </motion.div>
      </div>
    </div>
  );
}
